import * as React from "react";
import { useDataProvider, useLocaleState } from "react-admin";
import CreatorPage from "../creatorsShared/CreatorPage";
import { createWithId } from "../creatorsShared/createWithId";
import { updateRecord } from "../creatorsShared/updateRecord";
import FaqCreatorBuilderForm from "./FaqCreatorBuilderForm";
import FaqCreatorRender from "./FaqCreatorRender";
import { Faq, Translatable, WithId } from "../../types";

interface FaqOption extends WithId {
  sectionTitle?: Translatable<string>;
}

interface EditingIds {
  faqId: string;
  bannerId: string;
}

const emptyDefaults = {
  banner: {},
  faq: {},
};

const FaqCreatorPage = () => {
  const dataProvider = useDataProvider();
  const [locale] = useLocaleState();

  const loadRecord = async (option: FaqOption) => {
    const { data: faq } = await dataProvider.getOne<Faq>("faq", {
      id: option.id,
    });
    // Same dataProvider.getOne id-clobbering quirk as the services creator
    // - keep the ids we already know. See PreviewPage.tsx for the full note.
    const bannerId = faq.banner;
    const { data: banner } = await dataProvider.getOne("banners", {
      id: bannerId,
    });

    return {
      defaultValues: {
        banner: { ...banner },
        faq: { ...faq },
      },
      editingIds: { faqId: option.id, bannerId },
    };
  };

  const saveRecord = async (
    values: Record<string, any>,
    editingIds: EditingIds | null
  ) => {
    const bannerPayload = {
      bannerImage: values.banner?.bannerImage,
      bannerTitle: values.banner?.bannerTitle,
      bannerSubtitle: values.banner?.bannerSubtitle,
      bannerSlogans: values.banner?.bannerSlogans,
    };

    let bannerId: string;
    if (editingIds) {
      await updateRecord(dataProvider, "banners", editingIds.bannerId, bannerPayload);
      bannerId = editingIds.bannerId;
    } else {
      bannerId = await createWithId("banners", bannerPayload);
    }

    const faqPayload = {
      banner: bannerId,
      sectionTitle: values.faq?.sectionTitle,
      sectionSubtitle: values.faq?.sectionSubtitle,
      faq: values.faq?.faq,
    };

    let faqId: string;
    if (editingIds) {
      await updateRecord(dataProvider, "faq", editingIds.faqId, faqPayload);
      faqId = editingIds.faqId;
    } else {
      faqId = await createWithId("faq", faqPayload);
    }

    return { resource: "faq", id: faqId };
  };

  return (
    <CreatorPage<FaqOption, EditingIds>
      titleKey="faqCreator.title"
      subtitleKey="faqCreator.subtitle"
      saveKey="faqCreator.save"
      saveExistingKey="faqCreator.saveExisting"
      successKey="faqCreator.notifications.success"
      errorKey="faqCreator.notifications.error"
      loadErrorKey="faqCreator.notifications.loadError"
      selector={{
        resource: "faq",
        getOptionLabel: (option) => option.sectionTitle?.[locale],
        labelKey: "faqCreator.selector.label",
        placeholderKey: "faqCreator.selector.new",
      }}
      emptyDefaults={emptyDefaults}
      loadRecord={loadRecord}
      saveRecord={saveRecord}
      BuilderForm={FaqCreatorBuilderForm}
      RenderPreview={FaqCreatorRender}
    />
  );
};

export default FaqCreatorPage;
